import { MockEntityObjectCreator } from "@/testing";
import { IEventDataSource, IEventModifier, IGameOrganizer } from "@/types/interfaces";
import { BigBashEvent, Customer, InputEventDetails, Player, Team } from "@/types/types";
import { idTimestampManager } from "@utils/IDTimestampManager";
import SmallHelpers from "./SmallHelpers";

class EventModifier implements IEventModifier {
	smallHelpers: SmallHelpers;

	constructor(
		private eventDataSource: IEventDataSource,
		private gameOrganizer: IGameOrganizer,
		private mockEntityObjectCreator: MockEntityObjectCreator
	) {
		this.smallHelpers = new SmallHelpers();
	}

	public async createEvent(): Promise<BigBashEvent> {
		const newEvent = this.mockEntityObjectCreator.createEmptyEvent();
		const eventWithGames = await this._addDefaultGamesToEvent(newEvent);
		const eventWithGamesAndOrder = this._addDefaultGameOrderToEvent(eventWithGames);
		const eventWithDefaultCustomer = await this._addDefaultCustomerToEvent(eventWithGamesAndOrder);
		const eventWithDuration = { ...eventWithDefaultCustomer, duration: 90 };
		return eventWithDuration;
	}

	public addOrUpdateTeam(event: BigBashEvent, teamIndex: number, inputEventDetails: InputEventDetails): BigBashEvent {
		const newOrUpdatedTeam: Team = this._createOrUpdateTeam(event.teams, teamIndex, inputEventDetails);
		const newTeams = [...event.teams];
		newTeams[teamIndex] = newOrUpdatedTeam;
		return {
			...event,
			teams: newTeams
		};
	}

	public deleteTeam(event: BigBashEvent, teamIndex: number): BigBashEvent {
		const newTeams = event.teams.filter((_, index) => index !== teamIndex);
		return {
			...event,
			teams: newTeams
		};
	}

	public addOrUpdateCustomer(event: BigBashEvent, inputEventDetails: InputEventDetails): BigBashEvent {
		let customer: Customer = event.customer;
		if (!customer.id) {
			customer = { ...idTimestampManager.setInitialValues('Customer'), ...customer };
		}
		if (inputEventDetails.name in customer) {
			customer[inputEventDetails.name] = inputEventDetails.value ?? '';
			return {
				...event,
				customer: customer
			};
		}
		return event;
	}

	private _createOrUpdateTeam(teams: Team[], teamIndex: number, inputDetails: InputEventDetails): Team {
		const team: Team = this._getOrCreateTeam(teams, teamIndex);
		if (inputDetails.name in team) {
			team[inputDetails.name] = inputDetails.value ?? '';
		}
		if (inputDetails.name === 'players') {
			team.players = this._createPlayers(inputDetails);
		}
		return team;
	}

	private _getOrCreateTeam(teams: Team[], teamIndex: number): Team {
		if (teams[teamIndex]) {
			return teams[teamIndex];
		}
		return { ...idTimestampManager.setInitialValues('Team'), name: "", players: [], team_number: teamIndex + 1, color: this.smallHelpers.generateTeamColor(teamIndex) };
	}

	private _createPlayers(inputDetails: InputEventDetails): Player[] {
		const playerNames = this.smallHelpers.splitPlayerNames(inputDetails.value);
		return playerNames.map(name => ({
			...idTimestampManager.setInitialValues('Player'),
			name: name
		} as Player));
	}

	private async _addDefaultGamesToEvent(event: BigBashEvent): Promise<BigBashEvent> {
		const defaultGames = await this.eventDataSource.getDefaultGames();
		event.games = [...event.games, ...defaultGames];
		return event;
	}

	private async _addDefaultCustomerToEvent(event: BigBashEvent): Promise<BigBashEvent> {
		const defaultCustomer = await this.eventDataSource.getDefaultCustomer();
		event.customer = defaultCustomer;
		return event;
	}

	private _addDefaultGameOrderToEvent(event: BigBashEvent): BigBashEvent {
		event.game_order = Object.fromEntries(this.gameOrganizer.createDefaultGameOrder(event.games));
		return event;
	}
}

export default EventModifier;